import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Save, Loader2, Image as ImageIcon, X, Upload, Trash2, GripVertical } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CONDITIONS = ['Sehr gut', 'Gut', 'Gebraucht', 'Restaurationsobjekt'];
const MAX_IMAGES = 20;

function SortableImage({ img, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: img.id || img.url });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };
  return (
    <div ref={setNodeRef} style={style} className="relative aspect-square overflow-hidden border border-border group">
      <div {...attributes} {...listeners} className="absolute top-1 left-1 w-6 h-6 bg-black/60 text-white rounded flex items-center justify-center cursor-grab z-10 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`drag-handle-${img.id}`}>
        <GripVertical size={14} />
      </div>
      <img src={img.url} alt="" className="w-full h-full object-cover" />
      <button onClick={() => onDelete(img.id)} className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" title="Löschen">
        <X size={14} />
      </button>
    </div>
  );
}

export default function ListingEditPage() {
  const { listingId } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = !listingId;
  const navigate = useNavigate();
  const getToken = () => localStorage.getItem('shrt_token');

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [realId, setRealId] = useState(listingId);
  const fileRef = useRef();

  const [type, setType] = useState(searchParams.get('type') || 'sell');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [mileage, setMileage] = useState('');
  const [condition, setCondition] = useState('');
  const [price, setPrice] = useState('');
  const [priceOnRequest, setPriceOnRequest] = useState(false);
  const [location, setLocation] = useState('');
  const [viewingPossible, setViewingPossible] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [externalLink, setExternalLink] = useState('');
  const [images, setImages] = useState([]);
  // Search-specific
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');
  const [budgetMax, setBudgetMax] = useState('');

  useEffect(() => {
    if (!isNew) fetchListing();
  }, [listingId]);

  const fetchListing = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/marketplace/listings/${listingId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const l = res.data;
      setType(l.type);
      setTitle(l.title);
      setDescription(l.description || '');
      setBrand(l.brand || '');
      setModel(l.model || '');
      setYear(l.year || '');
      setMileage(l.mileage || '');
      setCondition(l.condition || '');
      setPrice(l.price != null ? String(l.price) : '');
      setPriceOnRequest(l.price_on_request || false);
      setLocation(l.location || '');
      setViewingPossible(l.viewing_possible || false);
      setShowContact(l.show_contact || false);
      setExternalLink(l.external_link || '');
      setImages(l.images || []);
      setYearFrom(l.year_from || '');
      setYearTo(l.year_to || '');
      setBudgetMax(l.budget_max != null ? String(l.budget_max) : '');
      setRealId(l.id);
    } catch { toast.error('Inserat nicht gefunden'); navigate('/marketplace'); }
    finally { setLoading(false); }
  };

  const save = async () => {
    if (!title.trim()) { toast.error('Titel ist Pflichtfeld'); return; }
    setSaving(true);
    try {
      const payload = {
        type, title, description, brand, model,
        year, mileage, condition,
        price: price ? parseFloat(price) : null,
        price_on_request: priceOnRequest,
        location, viewing_possible: viewingPossible,
        show_contact: showContact,
        external_link: externalLink,
        year_from: yearFrom, year_to: yearTo,
        budget_max: budgetMax ? parseFloat(budgetMax) : null,
      };

      if (isNew && !realId) {
        const res = await axios.post(`${API_URL}/api/marketplace/listings`, payload, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        setRealId(res.data.listing.id);
        toast.success('Inserat erstellt');
        navigate(`/marketplace/${res.data.listing.id}/edit`, { replace: true });
      } else {
        await axios.put(`${API_URL}/api/marketplace/listings/${realId}`, payload, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        toast.success('Inserat gespeichert');
      }
    } catch { toast.error('Fehler beim Speichern'); }
    finally { setSaving(false); }
  };

  const uploadImage = async (file) => {
    if (!realId) { toast.error('Bitte zuerst speichern'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/marketplace/listings/${realId}/upload-image`, fd, {
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'multipart/form-data' }
      });
      setImages(res.data.images);
      return res.data.images;
    } catch { toast.error('Fehler beim Hochladen'); }
    finally { setUploading(false); }
  };

  const uploadMultipleImages = async (files) => {
    if (!realId) { toast.error('Bitte zuerst speichern'); return; }
    const fileList = Array.from(files);
    const remaining = MAX_IMAGES - images.length;
    if (remaining <= 0) { toast.error(`Maximal ${MAX_IMAGES} Bilder erlaubt`); return; }
    const toUpload = fileList.slice(0, remaining);
    if (toUpload.length < fileList.length) {
      toast.info(`Nur ${toUpload.length} von ${fileList.length} Bildern werden hochgeladen (max. ${MAX_IMAGES})`);
    }
    setUploading(true);
    let latest = images;
    for (const file of toUpload) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API_URL}/api/marketplace/listings/${realId}/upload-image`, fd, {
          headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'multipart/form-data' }
        });
        latest = res.data.images;
        setImages(latest);
      } catch { toast.error(`Fehler bei "${file.name}"`); }
    }
    setUploading(false);
    if (toUpload.length > 1) toast.success(`${toUpload.length} Bilder hochgeladen`);
    else if (toUpload.length === 1) toast.success('Bild hochgeladen');
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const files = e.dataTransfer?.files;
    if (files?.length) {
      const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
      if (imageFiles.length) uploadMultipleImages(imageFiles);
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragOver(false); };

  const deleteImage = async (imageId) => {
    try {
      const res = await axios.delete(`${API_URL}/api/marketplace/listings/${realId}/images/${imageId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setImages(res.data.images);
    } catch { toast.error('Fehler'); }
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = images.findIndex(i => (i.id || i.url) === active.id);
    const newIdx = images.findIndex(i => (i.id || i.url) === over.id);
    const reordered = arrayMove(images, oldIdx, newIdx);
    setImages(reordered);
    try {
      await axios.put(`${API_URL}/api/marketplace/listings/${realId}/reorder-images`,
        { image_ids: reordered.map(i => i.id) },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
    } catch { toast.error('Fehler bei der Sortierung'); }
  };

  if (loading) return <Layout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;

  const isSell = type === 'sell';

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate('/marketplace')} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" /> Marktplatz
          </button>
          <Button onClick={save} disabled={saving} className="rounded-none uppercase tracking-wider" data-testid="save-listing-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Speichern
          </Button>
        </div>

        <h1 className="font-['Oswald'] text-2xl font-bold uppercase tracking-wide mb-6">
          {isNew ? (isSell ? 'Fahrzeug verkaufen' : 'Gesuch aufgeben') : 'Inserat bearbeiten'}
        </h1>

        {/* Type toggle */}
        {isNew && (
          <div className="flex gap-2 mb-6">
            <button onClick={() => setType('sell')} className={`flex-1 py-2 text-sm font-semibold uppercase tracking-wider border transition-colors ${isSell ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'}`}>
              Verkaufen
            </button>
            <button onClick={() => setType('search')} className={`flex-1 py-2 text-sm font-semibold uppercase tracking-wider border transition-colors ${!isSell ? 'border-blue-500 bg-blue-500/10 text-blue-500' : 'border-border text-muted-foreground'}`}>
              Suche
            </button>
          </div>
        )}

        {/* Main form */}
        <div className="bg-card border border-border p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Titel *</label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder={isSell ? 'z.B. Porsche 911 Carrera RS 2.7' : 'z.B. Suche Alfa Romeo GTV'} className="rounded-none" data-testid="listing-title" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Marke</label>
              <Input value={brand} onChange={e => setBrand(e.target.value)} placeholder="z.B. Porsche" className="rounded-none" data-testid="listing-brand" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Modell</label>
              <Input value={model} onChange={e => setModel(e.target.value)} placeholder="z.B. 911 Carrera" className="rounded-none" data-testid="listing-model" />
            </div>
          </div>

          {isSell ? (
            <>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Baujahr</label>
                  <Input value={year} onChange={e => setYear(e.target.value)} placeholder="1973" className="rounded-none" data-testid="listing-year" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Kilometerstand</label>
                  <Input value={mileage} onChange={e => setMileage(e.target.value)} placeholder="z.B. 85'000 km" className="rounded-none" data-testid="listing-mileage" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Zustand</label>
                  <select value={condition} onChange={e => setCondition(e.target.value)} className="w-full bg-background border border-input px-3 py-2 text-sm focus:outline-none" data-testid="listing-condition">
                    <option value="">-- Wählen --</option>
                    {CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 items-end">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Preis (CHF)</label>
                  <Input value={price} onChange={e => setPrice(e.target.value)} placeholder="z.B. 85000" type="number" className="rounded-none" disabled={priceOnRequest} data-testid="listing-price" />
                </div>
                <label className="flex items-center gap-2 pb-2 cursor-pointer select-none">
                  <input type="checkbox" checked={priceOnRequest} onChange={e => setPriceOnRequest(e.target.checked)} className="w-4 h-4" />
                  <span className="text-sm">Preis auf Anfrage</span>
                </label>
              </div>
              <div className="grid grid-cols-2 gap-4 items-end">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Standort</label>
                  <Input value={location} onChange={e => setLocation(e.target.value)} placeholder="z.B. Solothurn, CH" className="rounded-none" data-testid="listing-location" />
                </div>
                <label className="flex items-center gap-2 pb-2 cursor-pointer select-none">
                  <input type="checkbox" checked={viewingPossible} onChange={e => setViewingPossible(e.target.checked)} className="w-4 h-4" data-testid="listing-viewing" />
                  <span className="text-sm">Besichtigung möglich</span>
                </label>
              </div>
              <label className="flex items-center gap-2 cursor-pointer select-none mt-2 bg-muted/30 p-3 border border-border">
                <input type="checkbox" checked={showContact} onChange={e => setShowContact(e.target.checked)} className="w-4 h-4" data-testid="listing-show-contact" />
                <div>
                  <span className="text-sm font-medium">Kontaktdaten auf öffentlicher Seite anzeigen</span>
                  <p className="text-xs text-muted-foreground">Name, E-Mail und Telefon werden auf der öffentlich geteilten Seite sichtbar</p>
                </div>
              </label>
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Externer Link</label>
                <Input value={externalLink} onChange={e => setExternalLink(e.target.value)} placeholder="z.B. https://www.bringatrailer.com/listing/..." className="rounded-none" data-testid="listing-external-link" />
                <p className="text-xs text-muted-foreground mt-1">Optionaler Link zu einer externen Seite (z.B. Auktion, Originalinserat)</p>
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Baujahr von</label>
                  <Input value={yearFrom} onChange={e => setYearFrom(e.target.value)} placeholder="1960" className="rounded-none" data-testid="listing-year-from" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Baujahr bis</label>
                  <Input value={yearTo} onChange={e => setYearTo(e.target.value)} placeholder="1980" className="rounded-none" data-testid="listing-year-to" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Budget max. (CHF)</label>
                  <Input value={budgetMax} onChange={e => setBudgetMax(e.target.value)} placeholder="z.B. 50000" type="number" className="rounded-none" data-testid="listing-budget" />
                </div>
              </div>
            </>
          )}

          <div>
            <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Beschreibung</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder={isSell ? 'Zustand, Ausstattung, Historie...' : 'Was genau suchst du?'} className="w-full bg-background border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary min-h-[100px] resize-y" data-testid="listing-description" />
          </div>
        </div>

        {/* Images (sell only) */}
        {isSell && (
          <div
            className={`bg-card border ${dragOver ? 'border-primary border-2' : 'border-border'} p-6 mt-4 transition-colors`}
            onDrop={handleFileDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider">Bilder</label>
              <span className={`text-xs font-mono ${images.length >= MAX_IMAGES ? 'text-red-500' : 'text-muted-foreground'}`} data-testid="image-counter">
                {images.length}/{MAX_IMAGES}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Bilder per Drag & Drop sortieren oder Dateien hierher ziehen. Das erste Bild wird als Titelbild verwendet.</p>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={images.map(i => i.id || i.url)} strategy={rectSortingStrategy}>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-3">
                  {images.map(img => (
                    <SortableImage key={img.id || img.url} img={img} onDelete={deleteImage} />
                  ))}
                  {images.length < MAX_IMAGES && (
                    <button
                      onClick={() => fileRef.current?.click()}
                      className={`aspect-square border-2 border-dashed ${dragOver ? 'border-primary text-primary' : 'border-border text-muted-foreground hover:border-primary hover:text-primary'} transition-colors flex flex-col items-center justify-center gap-1`}
                      disabled={uploading || !realId}
                      data-testid="upload-image-btn"
                    >
                      {uploading ? <Loader2 size={20} className="animate-spin" /> : <Upload size={20} />}
                      <span className="text-xs">{uploading ? 'Laden...' : 'Hinzufügen'}</span>
                    </button>
                  )}
                </div>
              </SortableContext>
            </DndContext>
            {!realId && <p className="text-xs text-muted-foreground">Speichere das Inserat zuerst, um Bilder hochzuladen.</p>}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={e => {
                const files = e.target.files;
                if (files?.length) uploadMultipleImages(files);
                e.target.value = '';
              }}
            />
          </div>
        )}
      </div>
    </Layout>
  );
}
